import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Group, Stack, TextInput, Title, Select, Table, ActionIcon, Modal, Badge, Checkbox, Loader, Text, Progress } from '@mantine/core';
import { IconPlus, IconTrash, IconPencil, IconSearch, IconHistory } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { Asset, UpsertAsset } from '../services/assetsService';
import { listAssets, createAsset, updateAsset, deleteAsset, listAssetTypes, listAssetValueTypes, listAssetDatatypes } from '../services/assetsService';
import { listCustomers } from '../services/customersService';

const formatDateTime = (dateString?: string) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleString();
  } catch {
    return '-';
  }
};

const AssetsPage: React.FC = () => {
  const translation = useTranslation();
  const t = translation?.t || ((key: string) => key);
  const navigate = useNavigate();
  const [items, setItems] = useState<Asset[]>([]);
  const [customers, setCustomers] = useState<{ value: string; label: string }[]>([]);
  const [assetTypes, setAssetTypes] = useState<{ value: string; label: string }[]>([]);
  const [assetValueTypes, setAssetValueTypes] = useState<{ value: string; label: string }[]>([]);
  const [assetDatatypes, setAssetDatatypes] = useState<{ value: string; label: string }[]>([]);
  const [form, setForm] = useState<UpsertAsset>({ sn: '', type: '', install_name: '', mac: '', customerId: '', active: true, value_type_id: undefined, datatype_id: undefined });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    sn: '',
    type: '',
    install_name: '',
    mac: '',
    customerId: '',
    value_type_name: '',
    datatype_name: '',
    active: 'all' // 'all', 'active', 'inactive'
  });
  
  const emptyForm: UpsertAsset = { sn: '', type: '', install_name: '', mac: '', customerId: '', active: true, value_type_id: undefined, datatype_id: undefined };

  const load = async () => {
    setLoading(true);
    setError(null);
    setLoadingStage(t('common.loading.assets') || 'Loading assets...');
    
    try {
      setLoadingStage(t('common.loading.data') || 'Loading data...');
      const [a, c, types, valueTypes, datatypes] = await Promise.all([
        listAssets(), 
        listCustomers(), 
        listAssetTypes(), 
        listAssetValueTypes(), 
        listAssetDatatypes()
      ]);
      
      setLoadingStage(t('common.loading.processing') || 'Processing...');
      setItems(a.items);
      setCustomers(c.items.map((x) => ({ value: x.id, label: x.company })));
      setAssetTypes(types.map((t) => ({ value: t.name, label: t.name })));
      setAssetValueTypes(valueTypes.map((vt) => ({ value: vt.id, label: vt.name })));
      setAssetDatatypes(datatypes.map((dt) => ({ value: dt.id, label: dt.name })));
      
      setLoadingStage(t('common.loading.complete') || 'Complete');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg || 'Failed to load assets');
    } finally {
      setLoading(false);
      setLoadingStage('');
    }
  };

  useEffect(() => { load(); }, []);

  // Filter items based on filters
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      // Text filters (case-insensitive)
      if (filters.sn && !item.sn.toLowerCase().includes(filters.sn.toLowerCase())) return false;
      if (filters.type && !item.type.toLowerCase().includes(filters.type.toLowerCase())) return false;
      if (filters.install_name && !item.install_name.toLowerCase().includes(filters.install_name.toLowerCase())) return false;
      if (filters.mac && !item.mac.toLowerCase().includes(filters.mac.toLowerCase())) return false;
      
      // Select filters
      if (filters.customerId && item.customerId !== filters.customerId) return false;
      if (filters.value_type_name && item.value_type_name !== filters.value_type_name) return false;
      if (filters.datatype_name && item.datatype_name !== filters.datatype_name) return false;
      
      // Active filter
      if (filters.active === 'active' && !item.active) return false;
      if (filters.active === 'inactive' && item.active) return false;
      
      return true;
    });
  }, [items, filters]);

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      sn: '',
      type: '',
      install_name: '',
      mac: '',
      customerId: '',
      value_type_name: '',
      datatype_name: '',
      active: 'all'
    });
  };

  // Note: removed async autocomplete; using a simple dropdown with initially loaded customers

  const openEdit = (a: Asset) => { 
    setEditingId(a.id); 
    setForm({ 
      sn: a.sn || '', 
      type: a.type || '', 
      install_name: a.install_name || '', 
      mac: a.mac || '', 
      customerId: a.customerId || '',
      active: a.active,
      value_type_id: a.value_type_id,
      datatype_id: a.datatype_id
    }); 
    setModalOpen(true); 
  };

  const onSubmit = async () => {
    try {
      if (editingId) {
        await updateAsset(editingId, form);
      }
      setModalOpen(false);
      await load();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      alert(msg || 'Save failed');
    }
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center">
        <Title order={2}>{t('admin.assets.adminTitle') || 'Admin Assets'}</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => { setEditingId(null); setForm(emptyForm); setModalOpen(true); }}>{t('common.new') || 'Neu'}</Button>
      </Group>

      {/* Filters Section */}
      <Card withBorder>
        <Title order={4} mb="md">{t('common.filters') || 'Filter'}</Title>
        <Stack gap="sm">
          {/* First row of filters */}
          <Group grow>
            <TextInput
              label={t('admin.assets.serialNumber') || 'Serial number'}
              placeholder="Filter by serial number..."
              value={filters.sn}
              onChange={(e) => setFilters(f => ({ ...f, sn: e.target.value }))}
              leftSection={<IconSearch size={16} />}
            />
            <Select
              label={t('admin.assets.type') || 'Type'}
              placeholder="Filter by type..."
              value={filters.type}
              onChange={(v) => setFilters(f => ({ ...f, type: v || '' }))}
              data={assetTypes}
              clearable
            />
            <TextInput
              label={t('admin.assets.installName') || 'Install Name'}
              placeholder="Filter by install name..."
              value={filters.install_name}
              onChange={(e) => setFilters(f => ({ ...f, install_name: e.target.value }))}
              leftSection={<IconSearch size={16} />}
            />
          </Group>
          
          {/* Second row of filters */}
          <Group grow>
            <TextInput
              label={t('admin.assets.mac') || 'MAC'}
              placeholder="Filter by MAC..."
              value={filters.mac}
              onChange={(e) => setFilters(f => ({ ...f, mac: e.target.value }))}
              leftSection={<IconSearch size={16} />}
            />
            <Select
              label={t('users.customer') || 'Customer'}
              placeholder="Filter by customer..."
              value={filters.customerId}
              onChange={(v) => setFilters(f => ({ ...f, customerId: v || '' }))}
              data={customers}
              clearable
            />
            <Select
              label={t('admin.assets.valueType') || 'Value Type'}
              placeholder="Filter by value type..."
              value={filters.value_type_name}
              onChange={(v) => setFilters(f => ({ ...f, value_type_name: v || '' }))}
              data={assetValueTypes}
              clearable
            />
          </Group>
          
          {/* Third row of filters */}
          <Group grow>
            <Select
              label={t('admin.assets.datatype') || 'Datatype'}
              placeholder="Filter by datatype..."
              value={filters.datatype_name}
              onChange={(v) => setFilters(f => ({ ...f, datatype_name: v || '' }))}
              data={assetDatatypes}
              clearable
            />
            <Select
              label={t('common.status') || 'Status'}
              placeholder="Filter by status..."
              value={filters.active}
              onChange={(v) => setFilters(f => ({ ...f, active: v || 'all' }))}
              data={[
                { value: 'all', label: t('common.all') || 'All' },
                { value: 'active', label: t('common.active') || 'Active' },
                { value: 'inactive', label: t('common.inactive') || 'Inactive' }
              ]}
            />
            <Button variant="outline" onClick={clearFilters}>
              {t('common.clear') || 'Clear'}
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card withBorder>
        {error && (
          <Text c="red" mb="sm">{error}</Text>
        )}
        {loading && (
          <Stack align="center" justify="center" style={{ minHeight: 300 }}>
            <Loader size="xl" color="blue" />
            
            <Stack gap="sm" align="center" style={{ minWidth: 300 }}>
              <Text size="lg" fw={500}>
                {t('common.loading') || 'Loading...'}
              </Text>
              
              {loadingStage && (
                <Text size="sm" c="dimmed">
                  {loadingStage}
                </Text>
              )}
            </Stack>
          </Stack>
        )}
        {!loading && filteredItems.length === 0 ? (
          <div style={{ color: 'var(--mantine-color-dimmed)' }}>
            {items.length === 0 
              ? (t('data.table.noData') || 'Keine Daten')
              : (t('data.table.noData') || 'No assets match your filters')
            }
          </div>
        ) : (
          !loading && (
            <Table striped withRowBorders={false} highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('admin.assets.serialNumber') || 'Serial number'}</Table.Th>
                <Table.Th>{t('admin.assets.type') || 'Type'}</Table.Th>
                <Table.Th>{t('admin.assets.installName') || 'Install Name'}</Table.Th>
                <Table.Th>{t('admin.assets.mac') || 'MAC'}</Table.Th>
                <Table.Th>{t('users.customer') || 'Customer'}</Table.Th>
                <Table.Th>{t('admin.assets.valueType') || 'Value Type'}</Table.Th>
                <Table.Th>{t('admin.assets.datatype') || 'Datatype'}</Table.Th>
                <Table.Th>{t('common.status') || 'Status'}</Table.Th>
                <Table.Th style={{ width: 80 }}>{t('users.actions') || 'Actions'}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredItems.map((a) => (
                <Table.Tr key={a.id}>
                  <Table.Td style={{ opacity: a.active ? 1 : 0.6 }}>{a.sn}</Table.Td>
                  <Table.Td style={{ opacity: a.active ? 1 : 0.6 }}>{a.type}</Table.Td>
                  <Table.Td style={{ opacity: a.active ? 1 : 0.6 }}>{a.install_name}</Table.Td>
                  <Table.Td style={{ opacity: a.active ? 1 : 0.6 }}>{a.mac}</Table.Td>
                  <Table.Td style={{ opacity: a.active ? 1 : 0.6 }}>{customers.find(c => c.value === a.customerId)?.label || a.customerId}</Table.Td>
                  <Table.Td style={{ opacity: a.active ? 1 : 0.6 }}>{a.value_type_name || '-'}</Table.Td>
                  <Table.Td style={{ opacity: a.active ? 1 : 0.6 }}>{a.datatype_name || '-'}</Table.Td>
                  <Table.Td>
                    <Badge color={a.active ? 'green' : 'gray'} variant="light">
                      {a.active ? (t('common.active') || 'Aktiv') : (t('common.inactive') || 'Inaktiv')}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <ActionIcon variant="light" onClick={() => openEdit(a)}><IconPencil size={16} /></ActionIcon>
                      <ActionIcon 
                        variant="light" 
                        color="blue"
                        onClick={() => navigate(`/admin/assets/${a.id}/history`)}
                        title={t('version_history') || 'Version History'}
                      >
                        <IconHistory size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          )
        )}
      </Card>

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={editingId ? (t('admin.assets.edit') || 'Edit asset') : (t('admin.assets.create') || 'Create asset')}>
        <Stack>
          <TextInput label={t('admin.assets.serialNumber') || 'Serial number'} placeholder="20220001" value={form.sn || ''} onChange={(e) => setForm(f => ({ ...f, sn: e.target.value }))} />
          <Select
            label={t('admin.assets.type') || 'Type'}
            data={assetTypes}
            value={form.type || assetTypes[0]?.value || ''}
            onChange={(v: string | null) => {
              setForm(f => ({ ...f, type: v || '' }));
            }}
          />
          <Select
            label={t('admin.assets.valueType') || 'Value Type'}
            data={assetValueTypes}
            value={form.value_type_id?.toString() || ''}
            onChange={(v: string | null) => {
              setForm(f => ({ ...f, value_type_id: v ? parseInt(v) : undefined }));
            }}
            placeholder={t('common.selectValue') || 'Choose a value type...'}
            clearable
            disabled={form.type === 'Gateway'}
          />
                    <TextInput label={t('admin.assets.installName') || 'Install Name'} value={form.install_name || ''} onChange={(e) => setForm(f => ({ ...f, install_name: e.target.value }))} />
          <TextInput label={t('admin.assets.macAddress') || 'MAC Address'} placeholder="00:00:00:00:00:00" value={form.mac || ''} onChange={(e) => setForm(f => ({ ...f, mac: e.target.value }))} />
          <Select
            label={t('users.customer') || 'Customer'}
            data={customers}
            value={form.customerId || ''}
            onChange={(v: string | null) => setForm(f => ({ ...f, customerId: v || '' }))}
            comboboxProps={{ withinPortal: true }}
            placeholder={t('common.selectValue') || 'Choose a value...'}
          />
          <Checkbox
            label={t('common.active') || 'Aktiv'}
            checked={form.active}
            onChange={(e) => setForm(f => ({ ...f, active: e.currentTarget?.checked ?? false }))}
          />
          <Group justify="flex-end"><Button onClick={onSubmit}>{t('common.save') || 'Save'}</Button></Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default AssetsPage;
